import React, { useState, useEffect } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { usersAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Trophy, Star, Lock, Check, Award, 
  Zap, Target, Clock, Flame, Brain, Crown,
  Medal, TrendingUp
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Tier colors and styling
const TIER_STYLES = {
  gold: {
    bg: 'bg-gradient-to-br from-yellow-400 to-amber-600',
    border: 'border-yellow-400',
    text: 'text-yellow-400',
    glow: 'shadow-[0_0_15px_rgba(251,191,36,0.5)]',
    label: 'Gold'
  },
  silver: {
    bg: 'bg-gradient-to-br from-gray-300 to-gray-500',
    border: 'border-gray-400',
    text: 'text-gray-300',
    glow: 'shadow-[0_0_15px_rgba(156,163,175,0.4)]',
    label: 'Silver'
  },
  bronze: {
    bg: 'bg-gradient-to-br from-orange-400 to-orange-700',
    border: 'border-orange-400',
    text: 'text-orange-400',
    glow: 'shadow-[0_0_15px_rgba(251,146,60,0.4)]',
    label: 'Bronze'
  }
};

// Category icons
const CATEGORY_ICONS = {
  milestone: Trophy,
  accuracy: Target,
  speed: Zap,
  streak: Flame,
  category: Brain,
  difficulty: Crown,
  mastery: Award,
  volume: TrendingUp
};

export default function Achievements() {
  useSEO('Achievements & Badges | JepPrep', 'View your earned badges, achievements, and milestones from trivia training.');
  const [loading, setLoading] = useState(true);
  const [achievementData, setAchievementData] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [savingBadges, setSavingBadges] = useState(false);

  useEffect(() => {
    loadAchievements();
  }, []);

  const loadAchievements = async () => {
    try {
      const response = await usersAPI.getAchievements();
      setAchievementData(response.data);
      setSelectedBadges(response.data.featured_badges || []);
    } catch (error) {
      toast.error('Failed to load achievements');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFeaturedBadge = async (achievementId) => {
    let newSelection;
    if (selectedBadges.includes(achievementId)) {
      newSelection = selectedBadges.filter(id => id !== achievementId);
    } else if (selectedBadges.length < 5) {
      newSelection = [...selectedBadges, achievementId];
    } else {
      toast.error('Maximum 5 featured badges allowed');
      return;
    }
    
    setSelectedBadges(newSelection);
    setSavingBadges(true);
    
    try {
      await usersAPI.updateFeaturedBadges(newSelection);
      toast.success('Featured badges updated!');
    } catch (error) {
      toast.error('Failed to update badges');
      setSelectedBadges(achievementData.featured_badges || []);
    } finally {
      setSavingBadges(false);
    }
  };

  const getFilteredAchievements = () => {
    if (!achievementData?.progress) return [];
    
    if (activeTab === 'all') return achievementData.progress;
    if (activeTab === 'earned') return achievementData.progress.filter(p => p.earned);
    if (activeTab === 'locked') return achievementData.progress.filter(p => !p.earned);
    
    // Filter by category
    return achievementData.progress.filter(p => p.achievement.category === activeTab);
  };

  const AchievementCard = ({ data }) => {
    const { achievement, progress, earned } = data;
    const tier = TIER_STYLES[achievement.tier] || TIER_STYLES.bronze;
    const CategoryIcon = CATEGORY_ICONS[achievement.category] || Trophy;
    const isFeatured = selectedBadges.includes(achievement.id);

    return (
      <Card 
        className={`relative overflow-hidden transition-all duration-300 ${
          earned 
            ? `bg-[hsl(var(--secondary))] border-2 ${tier.border} ${tier.glow}` 
            : 'bg-[hsl(var(--secondary))]/50 border-[hsl(var(--border))] opacity-75'
        }`}
        data-testid={`achievement-card-${achievement.id}`}
      >
        {/* Tier Badge */}
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-bold ${tier.bg} text-white`}>
          {tier.label}
        </div>

        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {/* Badge Icon */}
            <div className={`relative w-16 h-16 rounded-full flex items-center justify-center ${
              earned ? tier.bg : 'bg-gray-700'
            }`}>
              {earned ? (
                <span className="text-3xl">{achievement.icon}</span>
              ) : (
                <Lock className="w-6 h-6 text-gray-500" />
              )}
              {earned && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Achievement Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CategoryIcon className={`w-4 h-4 ${earned ? tier.text : 'text-gray-500'}`} />
                <h3 className={`font-bold truncate ${earned ? 'text-white' : 'text-gray-400'}`}>
                  {achievement.name}
                </h3>
              </div>
              <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                {achievement.description}
              </p>

              {/* Progress Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Progress</span>
                  <span className={earned ? tier.text : 'text-gray-400'}>
                    {progress.percentage}%
                  </span>
                </div>
                <Progress 
                  value={progress.percentage} 
                  className="h-2 bg-gray-700"
                />
              </div>

              {/* Featured Badge Toggle (only for earned) */}
              {earned && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleFeaturedBadge(achievement.id)}
                  disabled={savingBadges}
                  className={`mt-2 text-xs ${
                    isFeatured 
                      ? 'text-[hsl(var(--brand-gold-500))] hover:text-[hsl(var(--brand-gold-400))]' 
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  <Star className={`w-3 h-3 mr-1 ${isFeatured ? 'fill-current' : ''}`} />
                  {isFeatured ? 'Featured' : 'Add to Profile'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading achievements...</div>
      </div>
    );
  }

  const filteredAchievements = getFilteredAchievements();
  const earnedCount = achievementData?.earned_count || 0;
  const totalCount = achievementData?.total_count || 0;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12" />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8 text-[hsl(var(--brand-gold-500))]" />
            <h1 className="text-3xl font-display font-bold text-white">Achievements</h1>
          </div>
          <p className="text-gray-400">
            Track your progress and showcase your trivia mastery
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Medal className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className="text-2xl font-bold text-white">{earnedCount}/{totalCount}</p>
              <p className="text-xs text-gray-400">Achievements Earned</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <div className={`w-8 h-8 mx-auto mb-2 rounded-full ${TIER_STYLES.gold.bg}`} />
              <p className="text-2xl font-bold text-white">
                {achievementData?.progress?.filter(p => p.earned && p.achievement.tier === 'gold').length || 0}
              </p>
              <p className="text-xs text-gray-400">Gold Badges</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <div className={`w-8 h-8 mx-auto mb-2 rounded-full ${TIER_STYLES.silver.bg}`} />
              <p className="text-2xl font-bold text-white">
                {achievementData?.progress?.filter(p => p.earned && p.achievement.tier === 'silver').length || 0}
              </p>
              <p className="text-xs text-gray-400">Silver Badges</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <div className={`w-8 h-8 mx-auto mb-2 rounded-full ${TIER_STYLES.bronze.bg}`} />
              <p className="text-2xl font-bold text-white">
                {achievementData?.progress?.filter(p => p.earned && p.achievement.tier === 'bronze').length || 0}
              </p>
              <p className="text-xs text-gray-400">Bronze Badges</p>
            </CardContent>
          </Card>
        </div>

        {/* Featured Badges Section */}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
              Featured Badges
            </CardTitle>
            <CardDescription className="text-gray-400">
              Select up to 5 achievements to display on your profile
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {selectedBadges.length > 0 ? (
                selectedBadges.map(badgeId => {
                  const achievementProgress = achievementData?.progress?.find(p => p.achievement.id === badgeId);
                  if (!achievementProgress) return null;
                  const achievement = achievementProgress.achievement;
                  const tier = TIER_STYLES[achievement.tier];
                  
                  return (
                    <div 
                      key={badgeId}
                      className={`w-16 h-16 rounded-full flex items-center justify-center ${tier.bg} ${tier.glow} cursor-pointer hover:scale-110 transition-transform`}
                      onClick={() => toggleFeaturedBadge(badgeId)}
                      title={achievement.name}
                    >
                      <span className="text-2xl">{achievement.icon}</span>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-sm">
                  Click "Add to Profile" on any earned achievement to feature it here
                </p>
              )}
              {selectedBadges.length > 0 && selectedBadges.length < 5 && (
                <div className="w-16 h-16 rounded-full border-2 border-dashed border-gray-600 flex items-center justify-center">
                  <span className="text-gray-600 text-xs">{5 - selectedBadges.length} left</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Achievement List */}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="text-white">All Achievements</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-[hsl(var(--accent))] border border-[hsl(var(--border))] mb-6 flex-wrap h-auto p-1">
                <TabsTrigger 
                  value="all"
                  className="text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  All ({totalCount})
                </TabsTrigger>
                <TabsTrigger 
                  value="earned"
                  className="text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  Earned ({earnedCount})
                </TabsTrigger>
                <TabsTrigger 
                  value="locked"
                  className="text-gray-300 data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  Locked ({totalCount - earnedCount})
                </TabsTrigger>
              </TabsList>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredAchievements.map((data) => (
                  <AchievementCard key={data.achievement.id} data={data} />
                ))}
              </div>

              {filteredAchievements.length === 0 && (
                <div className="text-center py-12">
                  <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No achievements found in this category</p>
                </div>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
